import {
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Typography,
  Chip,
  Paper,
} from '@mui/material';
import { Delete, Edit, CheckCircle, Cancel, Star } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Opportunity } from '@/types/opportunity';
import { getCategoryColorByCode } from '@config/category-colors';
import { getStarByColor } from '@config/healthworker-stars';

interface OpportunityListProps {
  opportunities: Opportunity[];
  onEdit?: (opportunity: Opportunity) => void;
  onDelete?: (opportunityId: string) => void;
}

export function OpportunityList({
  opportunities,
  onEdit,
  onDelete,
}: OpportunityListProps) {
  const { t } = useTranslation();

  if (opportunities.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          gap: 2,
          p: 3,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          {t('opportunityList.noOpportunities')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('opportunityList.tapToRecord')}
        </Typography>
      </Box>
    );
  }

  const sortedOpportunities = [...opportunities].sort(
    (a, b) => b.sequenceOrder - a.sequenceOrder
  );

  return (
    <List>
      {sortedOpportunities.map((opportunity) => (
        <Paper key={opportunity.opportunityId} sx={{ mb: 2, borderLeft: `4px solid ${getCategoryColorByCode(opportunity.observedHCWCode)}` }}>
          <ListItem
            secondaryAction={
              <Box sx={{ display: 'flex', gap: 1 }}>
                {onEdit && (
                  <IconButton edge="end" onClick={() => onEdit(opportunity)}>
                    <Edit />
                  </IconButton>
                )}
                {onDelete && (
                  <IconButton
                    edge="end"
                    onClick={() => onDelete(opportunity.opportunityId)}
                  >
                    <Delete />
                  </IconButton>
                )}
              </Box>
            }
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {opportunity.action.actionPerformed ? (
                    <CheckCircle color="success" />
                  ) : (
                    <Cancel color="error" />
                  )}
                  <Typography variant="subtitle1" fontWeight={600}>
                    #{opportunity.sequenceOrder} - {opportunity.observedHCWName}
                  </Typography>
                  {opportunity.healthworkerColor && (
                    <Star
                      sx={{
                        fontSize: 20,
                        color: getStarByColor(opportunity.healthworkerColor).color,
                      }}
                    />
                  )}
                </Box>
              }
              secondaryTypographyProps={{ component: 'div' }}
              secondary={
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {opportunity.observedHCWCode}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {opportunity.indications.map((ind) => {
                      return (
                        <Chip
                          key={ind}
                          label={t(`indications.${ind}.shortLabel`)}
                          size="small"
                          variant="outlined"
                        />
                      );
                    })}
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    {opportunity.action.actionPerformed ? (
                      <Chip
                        label={
                          opportunity.action.handrubUsed &&
                          opportunity.action.handwashUsed
                            ? t('actions.bothShort')
                            : opportunity.action.handrubUsed
                            ? t('actions.handrub')
                            : t('actions.handwash')
                        }
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip label={t('actions.missed')} color="error" size="small" />
                    )}
                    {opportunity.action.glovesWorn !== undefined && (
                      <Chip
                        label={
                          opportunity.action.glovesWorn
                            ? `${t('opportunityList.gloves')}: ${t('common.yes')}`
                            : `${t('opportunityList.gloves')}: ${t('common.no')}`
                        }
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                </Box>
              }
            />
          </ListItem>
        </Paper>
      ))}
    </List>
  );
}
